import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { TrainingAssignment, TrainingModule, PractitionerListItem, AssignmentStatus } from '../../types/training';
import { SearchableSelect } from '../../components/ui/SearchableSelect';

export const AssignmentManagement: React.FC = () => {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<TrainingAssignment[]>([]);
  const [practitioners, setPractitioners] = useState<PractitionerListItem[]>([]);
  const [modules, setModules] = useState<TrainingModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [moduleFilter, setModuleFilter] = useState<string>('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [assignmentsData, practitionersData, modulesData] = await Promise.all([
        adminService.getAssignments(),
        adminService.getPractitioners(),
        adminService.getModules(true), // Include archived modules so they display correctly in assignments
      ]);
      setAssignments(assignmentsData);
      setPractitioners(practitionersData);
      setModules(modulesData);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleUnassign = async (assignmentId: string) => {
    if (!window.confirm('Are you sure you want to remove this assignment?')) return;

    try {
      await adminService.unassignTraining(assignmentId);
      loadData();
    } catch (err: any) {
      alert(err.message || 'Failed to unassign training');
    }
  };

  const handleResetAttempts = async (assignmentId: string) => {
    const additionalAttempts = prompt('Enter additional attempts to grant:', '3');
    if (!additionalAttempts) return;

    try {
      await adminService.resetAttempts(assignmentId, parseInt(additionalAttempts));
      loadData();
    } catch (err: any) {
      alert(err.message || 'Failed to grant additional attempts');
    }
  };

  const filteredAssignments = assignments.filter((assignment) => {
    if (statusFilter !== 'all' && assignment.status !== statusFilter) return false;
    if (moduleFilter !== 'all' && assignment.moduleId !== moduleFilter) return false;
    return true;
  });

  const getPractitionerName = (practitionerId: string) => {
    const practitioner = practitioners.find((p) => p.id === practitionerId);
    return practitioner?.name || 'Unknown';
  };

  const getModuleName = (moduleId: string) => {
    const module = modules.find((m) => m.id === moduleId);
    return module?.title || 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate('/admin')}
        className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
      >
        <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Dashboard
      </button>
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Training Assignments</h2>
          <p className="mt-1 text-sm text-gray-600">Assign training modules to practitioners</p>
        </div>
        <button
          onClick={() => setShowAssignModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Assign Training
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700">
              Filter by Status
            </label>
            <select
              id="status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="all">All Statuses</option>
              <option value={AssignmentStatus.PENDING}>Pending</option>
              <option value={AssignmentStatus.IN_PROGRESS}>In Progress</option>
              <option value={AssignmentStatus.COMPLETED}>Completed</option>
              <option value={AssignmentStatus.FLAGGED}>Flagged</option>
            </select>
          </div>
          <div>
            <label htmlFor="module" className="block text-sm font-medium text-gray-700">
              Filter by Module
            </label>
            <select
              id="module"
              value={moduleFilter}
              onChange={(e) => setModuleFilter(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="all">All Modules</option>
              {modules.map((module) => (
                <option key={module.id} value={module.id}>
                  {module.title}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Assignments Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Practitioner
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Module
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Progress
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Assigned Date
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredAssignments.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                  No assignments found
                </td>
              </tr>
            ) : (
              filteredAssignments.map((assignment) => (
                <tr key={assignment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {getPractitionerName(assignment.practitionerId)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{getModuleName(assignment.moduleId)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        assignment.status === AssignmentStatus.COMPLETED
                          ? 'bg-green-100 text-green-800'
                          : assignment.status === AssignmentStatus.FLAGGED
                          ? 'bg-red-100 text-red-800'
                          : assignment.status === AssignmentStatus.IN_PROGRESS
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {assignment.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {assignment.progress.contentViewed ? 'Content Viewed' : 'Not Started'}
                    </div>
                    <div className="text-xs text-gray-500">
                      Attempts: {assignment.attempts}/{assignment.maxAttempts}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(assignment.assignedAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {assignment.flagged && (
                      <button
                        onClick={() => handleResetAttempts(assignment.id)}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        Reset
                      </button>
                    )}
                    {assignment.status !== AssignmentStatus.COMPLETED && (
                      <button
                        onClick={() => handleUnassign(assignment.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Unassign
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Assign Training Modal */}
      {showAssignModal && (
        <AssignTrainingModal
          practitioners={practitioners}
          modules={modules}
          onClose={() => setShowAssignModal(false)}
          onSuccess={() => {
            setShowAssignModal(false);
            loadData();
          }}
        />
      )}
    </div>
  );
};

interface AssignTrainingModalProps {
  practitioners: PractitionerListItem[];
  modules: TrainingModule[];
  onClose: () => void;
  onSuccess: () => void;
}

const AssignTrainingModal: React.FC<AssignTrainingModalProps> = ({
  practitioners,
  modules,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    practitionerId: '',
    moduleId: '',
    sendNotification: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter out archived modules
  const activeModules = modules.filter(m => !m.archived);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Find practitioner and module details
    const practitioner = practitioners.find(p => p.id === formData.practitionerId);
    const module = activeModules.find(m => m.id === formData.moduleId);

    if (!practitioner || !module) {
      setError('Invalid practitioner or module selected');
      return;
    }

    try {
      setLoading(true);
      await adminService.assignTraining({
        ...formData,
        practitionerName: practitioner.name,
        practitionerEmail: practitioner.email,
        moduleName: module.title,
      });
      onSuccess();
    } catch (err: any) {
      // Handle archived module error
      const errorMessage = err.response?.data?.error?.message || err.message || 'Failed to assign training';
      if (errorMessage.includes('archived')) {
        setError('This module has been archived and cannot be assigned to practitioners.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Assign Training</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="practitioner" className="block text-sm font-medium text-gray-700">
              Practitioner
            </label>
            <SearchableSelect
              id="practitioner"
              required
              value={formData.practitionerId}
              onChange={(value) => setFormData({ ...formData, practitionerId: value })}
              placeholder="Search for a practitioner..."
              options={practitioners.map((practitioner) => ({
                id: practitioner.id,
                label: `${practitioner.name} (${practitioner.email})`,
              }))}
            />
          </div>

          <div>
            <label htmlFor="module" className="block text-sm font-medium text-gray-700">
              Training Module
            </label>
            <select
              id="module"
              required
              value={formData.moduleId}
              onChange={(e) => setFormData({ ...formData, moduleId: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="">Select a module...</option>
              {activeModules.map((module) => (
                <option key={module.id} value={module.id}>
                  {module.title}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="sendNotification"
              checked={formData.sendNotification}
              onChange={(e) => setFormData({ ...formData, sendNotification: e.target.checked })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="sendNotification" className="ml-2 block text-sm text-gray-700">
              Send email notification to practitioner
            </label>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Assigning...' : 'Assign Training'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
